if (!customElements.get('slideshow-component')) {
  customElements.define(
    'slideshow-component',
    class SlideshowComponent extends HTMLElement {
      constructor() {
        super();

        this.sliderControls = this.querySelector('.swiper-controls');
        this.sliderInstance = false;
        this.enableSlider = this.dataset.enableSlider === 'true';
        this.selectedIndex = this.selectedIndex;
        this.sectionId = this.dataset.sectionId;

        if (!this.enableSlider) return;

        this.initSlider();
      }

      static get observedAttributes() {
        return ['selected-index'];
      }

      get selectedIndex() {
        return parseInt(this.getAttribute('selected-index')) || 0;
      }

      set selectedIndex(index) {
        this.setAttribute('selected-index', `${index}`);
      }

      initSlider() {
        const additionModules = [FoxTheme.Swiper.Autoplay, FoxTheme.Swiper.EffectFade];

        this.sliderOptions = {
          slidesPerView: 1,
          spaceBetween: 10,
          rewind: true,
          grabCursor: true,
          allowTouchMove: true,
          threshold: 2,
          effect: 'fade',
          on: {
            init: this.handleAfterInit.bind(this),
          },
          fadeEffect: {
            crossFade: true,
          },
        };

        if (this.sliderControls) {
          this.sliderOptions.navigation = {
            nextEl: this.sliderControls.querySelector('.swiper-button-next'),
            prevEl: this.sliderControls.querySelector('.swiper-button-prev'),
          };

          const isFractionPagination = this.sliderControls.classList.contains(
            'swiper-controls--pagination-fraction'
          );

          this.sliderOptions.pagination = {
            el: this.sliderControls.querySelector('.swiper-pagination'),
            clickable: !isFractionPagination,
          };

          if (isFractionPagination) {
            this.sliderOptions.pagination.type = 'fraction';
            this.sliderOptions.pagination.renderFraction = (currentClass, totalClass) => {
              return `<span class="${currentClass}"></span> / <span class="${totalClass}"></span>`;
            };
          }
        }

        const autoplayDelay = parseInt(this.dataset.autoplay);
        if (autoplayDelay > 0) {
          this.sliderOptions = {
            ...this.sliderOptions,
            autoplay: {
              delay: autoplayDelay,
              disableOnInteraction: false,
            },
          };
        }

        this.sliderInstance = new window.FoxTheme.Carousel(this, this.sliderOptions, additionModules);
        this.sliderInstance.init();

        this.sliderInstance.slider.on('realIndexChange', this.handleSlideChange.bind(this));

        if (this.sliderInstance) {
          this.selectedElement = this.sliderInstance.slider.slides[this.sliderInstance.slider.activeIndex];
          this.onReady(this.selectedElement, this.sliderInstance.slider.slides);

          // Fix accessibility
          const focusableElements = FoxTheme.a11y.getFocusableElements(this);
          focusableElements.forEach((element) => {
            if (!element.classList.contains('swiper-button') && !element.classList.contains('btn')) {
              element.addEventListener('focusin', () => {
                const slide = element.closest('.swiper-slide');
                this.sliderInstance.slider.slideTo(this.sliderInstance.slider.slides.indexOf(slide));
              });
            }
          });
        }
      }

      onReady(selectedElement) {
        if (selectedElement.dataset.type === 'video') {
          const videoElement = FoxTheme.utils.displayedMedia(selectedElement.querySelectorAll('video-element'));
          videoElement?.play();
        }

        if (!FoxTheme.config.motionReduced) {
          const motionEls = selectedElement.querySelectorAll('motion-element');
          motionEls.forEach((motionEl) => {
            setTimeout(() => {
              motionEl && motionEl.refreshAnimation();
            });
          });
        }
      }

      handleAfterInit() {
        this.removeAttribute('data-media-loading');
        this.restoreDeferredMedia();

        // Fix active bullet not transition on the first time (bullets pagination only).
        if (this.sliderControls && this.sliderControls.classList.contains('swiper-controls--pagination-bullets')) {
          const activeBullet = this.sliderControls.querySelector('.swiper-pagination-bullet-active');

          if (activeBullet) {
            activeBullet.classList.remove('swiper-pagination-bullet-active');
            activeBullet.offsetHeight; // Trigger reflow.
            activeBullet.classList.add('swiper-pagination-bullet-active');
          }
        }
      }

      /**
       * Non-first slides render with src/srcset renamed to data-deferred-* so
       * they aren't fetched during the initial page load, saving bandwidth
       * and main-thread work for images the visitor may never see. Restore
       * them once the page has fully loaded (or once they can plausibly be
       * shown, see below).
       */
      restoreDeferredMedia() {
        const restoreAll = () => this.querySelectorAll('.swiper-slide').forEach((slide) => this.restoreSlideMedia(slide));

        if (window.Shopify && window.Shopify.designMode) {
          restoreAll();
          return;
        }

        const events = ['pointerenter', 'touchstart', 'focusin'];
        const onInteract = () => {
          events.forEach((eventName) => this.removeEventListener(eventName, onInteract));
          restoreAll();
        };
        events.forEach((eventName) => this.addEventListener(eventName, onInteract, { passive: true }));

        // Autoplay: fetch one slide ahead, starting after load, so each image is ready before it is shown.
        if (parseInt(this.dataset.autoplay) > 0) {
          const restoreNext = () => {
            const { slides, activeIndex } = this.sliderInstance.slider;
            this.restoreSlideMedia(slides[(activeIndex + 1) % slides.length]);
          };
          const start = () => {
            restoreNext();
            this.sliderInstance.slider.on('slideChange', restoreNext);
          };

          if (document.readyState === 'complete') {
            start();
          } else {
            window.addEventListener('load', start, { once: true });
          }
        }
      }

      restoreSlideMedia(slide) {
        if (!slide) return;

        slide.querySelectorAll('[data-deferred-srcset], [data-deferred-src]').forEach((el) => {
          if (el.dataset.deferredSrcset) {
            el.setAttribute('srcset', el.dataset.deferredSrcset);
            el.removeAttribute('data-deferred-srcset');
          }
          if (el.dataset.deferredSrc) {
            el.setAttribute('src', el.dataset.deferredSrc);
            el.removeAttribute('data-deferred-src');
          }
        });
      }

      handleSlideChange(swiper) {
        const { slides, realIndex, activeIndex } = swiper;
        this.selectedIndex = realIndex;

        this.updateControlsScheme(slides[activeIndex]);
      }

      attributeChangedCallback(name, oldValue, newValue) {
        if (name === 'selected-index' && oldValue !== null && oldValue !== newValue) {
          const fromElements = this.querySelectorAll(`.swiper-slide[data-index="${oldValue}"]`);
          const toElements = this.querySelectorAll(`.swiper-slide[data-index="${newValue}"]`);

          fromElements.forEach((fromElement) => {
            if (fromElement.dataset.type === 'video') {
              const videoElement = FoxTheme.utils.displayedMedia([fromElement.querySelector('video-element')]);
              videoElement && videoElement.pause();
            }

            const motionEls = fromElement.querySelectorAll('motion-element');
            motionEls &&
              motionEls.forEach((motionEl) => {
                if (motionEl.hasAttribute('data-text')) {
                  motionEl.resetAnimation();
                }
              });
          });

          toElements.forEach((toElement) => {
            setTimeout(() => {
              if (toElement.classList.contains('swiper-slide-active')) {
                if (toElement.dataset.type === 'video') {
                  const videoElement = FoxTheme.utils.displayedMedia([toElement.querySelector('video-element')]);
                  videoElement && videoElement.play();
                }

                const motionEls = toElement.querySelectorAll('motion-element');
                motionEls.forEach((motionEl) => {
                  setTimeout(() => {
                    motionEl && motionEl.refreshAnimation();
                  });
                });
              }
            });
          });
        }
      }

      updateControlsScheme(activeSlide) {
        if (this.sliderControls) {
          const classesToRemove = Array.from(this.sliderControls.classList).filter((className) =>
            className.startsWith('color-')
          );
          classesToRemove.forEach((className) => this.sliderControls.classList.remove(className));
          const colorScheme = activeSlide.dataset.colorScheme;
          this.sliderControls.classList.add(colorScheme);
        }
      }
    }
  );
}
